/**
 * Plain-text analysis log — the artifact a surveyor files with the job.
 * Includes everything the session produced: solution, residuals, both
 * blunder-detection methods with the cross-method verdict, and transformed
 * points.
 */

import { formatDMS, formatAzimuth } from '@/logic/statistics';
import {
  deriveBlunderVerdict,
  type VerdictLimitation,
} from '@/logic/adjustments/blunderVerdict';
import type { SolutionMode } from '@/types';
import { prepareReportInput, type ReportInput } from './reportInput';

export const MODE_LABELS: Record<SolutionMode, string> = {
  standard: 'standard weighted',
  minimallyConstrained: 'one Fixed pair (minimally constrained)',
  constrained: 'two Fixed pairs (constrained)',
};

export const LIMITATION_TEXT: Record<VerdictLimitation, string> = {
  insufficientRedundancy:
    'data snooping ran out of redundancy, so remaining pairs were not tested.',
  lowPower:
    'the tau test had low power; a blunder near the MDB could pass undetected.',
  outdatedResults:
    'a result came from an earlier version of the methods; re-run detection.',
};

const RULE = '-'.repeat(70);

/** Residual with an explicit sign, e.g. "+0.475" / "-0.809". */
const signed = (value: number): string =>
  `${value >= 0 ? '+' : ''}${value.toFixed(3)}`;
const DOUBLE_RULE = '='.repeat(70);

export const buildAnalysisLog = (report: ReportInput): string => {
  const prepared = prepareReportInput(report);
  if (prepared.status === 'unavailable')
    return 'Report unavailable: point data changed. Re-solve before exporting.\n';
  const {
    inputRevision,
    solutionRevision,
    solutionInputRevision,
    appliedScale,
    units = 'unspecified',
    transformedFromRevision,
    solution,
    solutionTimestamp,
    pointPairs,
    blunderResults,
    dataSnoopingResults,
    transformedPoints,
  } = prepared.input;
  const omitted = prepared.omittedSections;
  let log = `Resolve Analysis Log — Cadastre\n`;
  log += `Units: ${units}\nInput revision: ${inputRevision ?? 'unknown'}; solution revision: ${solutionRevision ?? 'unknown'}; solved input revision: ${solutionInputRevision ?? 'unknown'}\nScale setting: ${appliedScale == null ? 'estimated' : appliedScale}\n`;
  if (transformedPoints.length)
    log += `Output uses solution revision: ${transformedFromRevision ?? 'unknown'}\n`;
  if (omitted.length)
    log += `Outdated sections omitted: ${omitted.join(', ')}\n`;
  log += `Generated: ${new Date().toLocaleString()}\n`;
  if (solutionTimestamp) {
    log += `Solution computed: ${new Date(solutionTimestamp).toLocaleString()}\n`;
  }
  log += `${DOUBLE_RULE}\n\n`;

  if (solution && !solution.error) {
    log += `TRANSFORMATION SOLUTION\n`;
    log += `${RULE}\n`;
    log += `Solution mode = ${MODE_LABELS[solution.mode ?? 'standard']}\n`;
    log += `Scale Factor = ${solution.scale.toFixed(8)}`;
    if (solution.sigmaScale !== undefined)
      log += ` Standard Deviation = ${solution.sigmaScale.toFixed(8)}`;
    else if (solution.scaleFixed) log += ' (held)';
    log += '\n';
    log += `Rotation = ${formatDMS(solution.rotation)}`;
    if (solution.sigmaRotation !== undefined)
      log += ` Standard Deviation = ${formatDMS(solution.sigmaRotation)}`;
    log += '\n';
    log += `Redundancy (degrees of freedom) = ${solution.redundancy}\n`;
    log += `Root Mean Square of Residuals = ${solution.rmsTotal.toFixed(4)}\n`;
    log +=
      solution.sigma0 !== undefined
        ? `Standard Error of Unit Weight (sigma0) = ${solution.sigma0.toFixed(4)}\n\n`
        : `Standard Error of Unit Weight (sigma0) = not estimable (no redundancy)\n\n`;

    log += `TRANSFORMATION EQUATIONS\n`;
    log += `N2 = A1*N1 - A2*E1 + A4\n`;
    log += `E2 = A2*N1 + A1*E1 + A3\n`;
    log += `A1 = ${solution.a.toFixed(10)}\n`;
    log += `A2 = ${solution.b.toFixed(10)}\n`;
    log += `A3 = ${solution.c.toFixed(5)}\n`;
    log += `A4 = ${solution.d.toFixed(5)}\n\n`;

    log += `RESIDUALS (Record → Field)\n`;
    log += `${'Record'.padEnd(8)} ${'Field'.padEnd(8)} ${'North'.padStart(10)} ${'East'.padStart(10)} ${'Azim'.padStart(6)} ${'Dist'.padStart(8)} ${'Status'.padStart(8)}\n`;
    log += `${RULE}\n`;

    solution.residuals.forEach((r) => {
      const pair = pointPairs.find((p) => p.id === r.id);
      if (pair && r.vn !== null) {
        log += `${pair.sys1Point.toString().padEnd(8)} `;
        log += `${pair.sys2Point.toString().padEnd(8)} `;
        log += `${signed(r.vn).padStart(10)} `;
        log += `${signed(r.ve!).padStart(10)} `;
        log += `${Math.round(r.azimuth!).toString().padStart(5)}° `;
        log += `${r.total!.toFixed(3).padStart(7)} `;
        log += `${r.status.padStart(7)}\n`;
      }
    });
    log += '\n';
  }

  // --- Blunder detection ---
  if (blunderResults || dataSnoopingResults) {
    log += `BLUNDER DETECTION\n`;
    log += `${DOUBLE_RULE}\n`;

    const verdict = deriveBlunderVerdict(
      blunderResults,
      dataSnoopingResults,
      pointPairs
    );
    const names = (points: { label: string }[]) =>
      points.map((p) => p.label).join(', ');

    log += `Cross-method verdict: `;
    switch (verdict.state) {
      case 'clean':
        log += `no blunders detected — both methods agree the data is consistent.\n`;
        break;
      case 'agree':
        log += `HIGH CONFIDENCE — both methods flag ${names(verdict.agreed)}.\n`;
        break;
      case 'inconclusive':
        log += `inconclusive — the matching results are not evidence of clean data.\n`;
        if (verdict.agreed.length > 0)
          log += `  Flagged by both: ${names(verdict.agreed)}\n`;
        break;
      case 'differ':
        log += `methods disagree.\n`;
        if (verdict.agreed.length > 0)
          log += `  Flagged by both (high confidence): ${names(verdict.agreed)}\n`;
        if (verdict.combinatorialOnly.length > 0)
          log += `  Combinatorial search only: ${names(verdict.combinatorialOnly)}\n`;
        if (verdict.dataSnoopingOnly.length > 0)
          log += `  Data snooping only: ${names(verdict.dataSnoopingOnly)}\n`;
        break;
      case 'single':
        log += `only one method was run — no cross-check available.\n`;
        break;
      default:
        log += `not run.\n`;
    }
    verdict.limitations.forEach((limitation) => {
      log += `  Limitation: ${LIMITATION_TEXT[limitation]}\n`;
    });
    log += '\n';

    if (blunderResults && !blunderResults.error) {
      log += `Combinatorial search`;
      if (blunderResults.isAllLevels && blunderResults.summary) {
        log += ` (best rejection set per level):\n`;
        log += `${'Level'.padEnd(7)} ${'Rejected'.padEnd(24)} ${'Max Resid'.padStart(10)} ${'Azim'.padStart(6)} ${'Rotation'.padStart(14)} ${'Scale'.padStart(10)}\n`;
        log += `${RULE}\n`;
        blunderResults.summary.forEach((row) => {
          log += `${String(row.level).padEnd(7)} `;
          log += `${(row.rejectedLabels?.join(', ') || '—').padEnd(24)} `;
          log += `${row.maxResidual.toFixed(3).padStart(10)} `;
          log += `${formatAzimuth(row.maxResidualAzimuth ?? 0).padStart(6)} `;
          log += `${formatDMS(row.rotation).padStart(14)} `;
          log += `${row.scale.toFixed(6).padStart(10)}\n`;
        });
      } else if (blunderResults.results) {
        log += ` (level ${blunderResults.rejectionLevel}, best first):\n`;
        blunderResults.results.slice(0, 5).forEach((row) => {
          log += `  ${row.isInitial ? 'no rejection' : row.rejectedLabels.join(', ')}: max residual ${row.maxResidual.toFixed(3)}\n`;
        });
      }
      log += '\n';
    }

    if (dataSnoopingResults && !dataSnoopingResults.error) {
      const { alpha, criticalValue, redundancy, testStatistic } =
        dataSnoopingResults;
      const confidence = ((1 - alpha) * 100).toFixed(1);
      const statistic = testStatistic === 'tau' ? 'tau' : 'w';
      log += `Iterative data snooping (${statistic === 'tau' ? "Pope's tau test" : 'w-test'}, ${confidence}% confidence per component`;
      if (criticalValue > 0)
        log += `, first critical value ${criticalValue.toFixed(2)}`;
      if (redundancy !== undefined) log += ` at r = ${redundancy}`;
      log += '):\n';
      dataSnoopingResults.iterations.forEach((iter) => {
        if (!iter.rejectedLabel) return;
        log += `  Iteration ${iter.iteration}: rejected ${iter.rejectedLabel}`;
        const details = [
          iter.standardizedResidual !== undefined &&
            `${statistic} = ${iter.standardizedResidual.toFixed(2)}`,
          iter.criticalValue !== undefined &&
            `critical ${iter.criticalValue.toFixed(2)}`,
          iter.minimumDetectableBias !== undefined &&
            `MDB = ${iter.minimumDetectableBias.toFixed(3)}`,
        ].filter(Boolean);
        if (details.length) log += ` (${details.join(', ')})`;
        log += '\n';
      });
      if (dataSnoopingResults.stopReason === 'insufficientRedundancy')
        log += `  Testing stopped: too little redundancy for the tau test.\n`;
      else if (dataSnoopingResults.stopReason === 'noTestableObservations')
        log += `  Testing stopped: no remaining residual could be tested.\n`;
      else if (dataSnoopingResults.finalRejectedIds.length === 0)
        log += `  All points passed the statistical test.\n`;
      if (dataSnoopingResults.lowPower)
        log += `  Low power: a blunder near the MDB could pass undetected.\n`;
      log += '\n';
    }
  }

  if (transformedPoints.length > 0) {
    log += `TRANSFORMED POINTS\n`;
    log += `${RULE}\n`;
    log += `Radial sigma: predicted position uncertainty (about 63%; x1.73 for 95%).\n`;
    log += `${'Pt#'.padEnd(8)} ${'North'.padStart(14)} ${'East'.padStart(14)} ${'Radial'.padStart(8)}\n`;
    transformedPoints.forEach((p) => {
      log += `${p.newPoint.padEnd(8)} ${p.n2.padStart(14)} ${p.e2.padStart(14)} ${(p.stdDev || '-').padStart(8)}\n`;
    });
  }

  return log;
};
